<template>
  <ion-app id="cmTestApp" @mouseleave="detectMouseLeave();"
           :class="(isMonitoringBrowserEnabled && isMobileDevice === false) ?'fullscreen-border':''">
    <div>
      <router-view v-if="!displayErrorPage && offline_status == STATUS_ONLINE && reach_sim_user_limit == false"
                   v-slot="{ Component }">
        <transition name="fade">
          <component :is="Component"></component>
        </transition>
      </router-view>

      <offline-mode
          v-else-if="!displayErrorPage && offline_status != STATUS_ONLINE && reach_sim_user_limit == false"></offline-mode>
      <sim-user-limit-message
          v-else-if="!displayErrorPage && offline_status != STATUS_ONLINE && reach_sim_user_limit"></sim-user-limit-message>
      <ErrorPage v-else></ErrorPage>

      <ion-modal
          :is-open="displayErrorModal"
          :backdrop-dismiss="false"
          :swipe-to-close="false"
          :css-class="$setComponentOrientation('global-error-modal')">
        <GlobalErrorModal></GlobalErrorModal>
      </ion-modal>


      <MonitoringErrorModal v-if="isMonitoringBrowserEnabled || isMonitoringCameraEnabled"/>
      <MonitoringCamera v-if="isMonitoringCameraEnabled"/>


      <PreviewModeBanner v-if="preview_mode == true"></PreviewModeBanner>

      <transition name="fade">
        <border-left-alert
            v-if="testState == constant.STATUS_TEST_IN_PROGRESS && (isMouseOnOuterBorder || isFaceOutOfWebcam)">

          <div>
            <img v-if="isFaceOutOfWebcam" src="@/assets/icons/no-face-white.svg">
            <img v-else src="@/assets/icons/cursor-select-white.svg">
          </div>

          <div>
            {{ leaveWarningMessage }}
          </div>

        </border-left-alert>
      </transition>

      <div id="print-content">
      </div>
    </div>
  </ion-app>
</template>

<script>
import {IonApp, IonModal, IonRouterOutlet} from '@ionic/vue';
import {computed, defineComponent, getCurrentInstance, provide, ref, watch} from 'vue';
import {mapGetters} from "vuex";

import MonitoringErrorModal from "./components/html/MonitoringErrorModal.vue";
import MonitoringCamera from "@/components/html/MonitoringCamera.vue";
import store from "@/store";
import htmlSetupUtil from "./utils/htmlSetupUtil";
import ErrorPage from "./views/ErrorPage";
import PreviewModeBanner from "./components/html/PreviewModeBanner";
import OfflineMode from "./components/test/OfflineMode";
import constant from "./constant";
import googleTagManagerUtils from "./utils/googleTagManagerUtils";
import SimUserLimitMessage from "@/components/test/SimUserLimitMessage.vue";
import {useMonitorBrowser, UseMonitorBrowserOptions} from "@/core/useMonitor/browser";

import MobileDetector from "./utils/mobileDetector";
import * as pluginConfig from "@/plugins/cookie-consent/config";
import {setCookieConsentFromCookie} from "@/plugins/cookie-consent/config";
import BorderLeftAlert from "@/components/test/BorderLeftAlert.vue";
import {useGtm} from "@gtm-support/vue-gtm";

export default defineComponent({
  name: 'App',
  components: {
    BorderLeftAlert,
    MonitoringCamera,
    MonitoringErrorModal,
    OfflineMode,
    ErrorPage,
    IonApp,
    IonRouterOutlet,
    IonModal,
    PreviewModeBanner,
    SimUserLimitMessage
  },
  mounted() {
    // remove initial loader
    let loader = document.querySelector("#initial_loader");
    if (loader != null) {
      loader.parentElement.removeChild(loader);
    }

    const {setupLanguage} = htmlSetupUtil();
    // // disable copy & paste if it is required.
    // disablePaste();
    // disableCopy();
    setupLanguage();

    /**   Print Pages - Override Ionic Css attributes using javascript     ***********************
     *
     * Ideally we shouldn't copy the content of the pages into extra div (#print-content) only for print purpose.
     * However some reasons, the questions are overlapped and couldn't find a solution except this way.
     *
     *
     * Below code copy the content of "ion-content" and past into "#print-content" before printing.
     * After print is done, the content of "#print-content" is removed
     * **/

    function beforePrint() {
      let settings = store.getters.testSettings;
      if (settings != null && settings.print_allowed && !store.state.print_button_clicked) {
        let ion_content = document.querySelector('ion-content');
        let print_content = document.querySelector('#print-content');
        print_content.innerHTML = ion_content.innerHTML;
      }
    }

    function afterPrint() {
      let print_content = document.querySelector('#print-content');
      print_content.innerHTML = '';
    }

    // set cypress test mode or not
    let cypress_test = false;
    if (window.Cypress) {
      cypress_test = true;
    }


    if(cypress_test  === false && store.getters.theme.show_ads === true) {
      /** Show Cookie consent banner or setup consent mode if cookie is set already **/
      let cookieContentsCookie = this.$CookieConsent.getCookie();
      if ((cookieContentsCookie.hasOwnProperty('categories')) === false) {
        // Run Cookie Consent Banner.
        this.$CookieConsent.run(pluginConfig.cookieConsentConfig);
        this.$CookieConsent.show(true);
      } else {
        setCookieConsentFromCookie();
      }
    }


    window.onbeforeprint = function (event) {
      beforePrint()
    }
    window.onafterprint = function (event) {
      afterPrint()
    }

    /************************************************************************************************/
    if (!this.isMonitoringBrowserEnabled) {
      window.addEventListener('beforeunload', this.$popupUnLoadEvent);
    }
  },
  beforeUnmount() {
    if (!this.isMonitoringBrowserEnabled) {
      window.removeEventListener('beforeunload', this.$popupUnLoadEvent);
    }
  },
  computed: {
    ...mapGetters('Monitoring', [
      'isMonitoringBrowserEnabled',
      'isMonitoringCameraEnabled',
    ])
  },


  setup() {


    const testState = computed(() => {
      return store.getters.testState;
    })
    const STATUS_ONLINE = constant.STATUS_ONLINE;

    // flag to show error page
    const displayErrorPage = computed(() => {
      return store.getters.displayErrorPage;
    })

    // flag to show error modal
    const displayErrorModal = computed(() => {
      return store.getters.displayErrorModal;
    });

    // test settings
    const settings = store.getters.testSettings;
    const language_set = computed(() => {
      return store.getters.languageSet
    });

    // flag to show preview mode
    const preview_mode = computed(() => {
      return store.getters.previewMode
    });

    //constant offline status
    const offline_status = computed(() => {
      return store.getters.offlineStatus;
    })


    const reach_sim_user_limit = computed(() => {
      return store.getters.reachSimUserLimit;
    })

    // SETUP Themes properties
    const theme = store.getters.theme;

    if (theme != null && theme.show_ads) {
      provide('show_ads', true)
      if (theme.test_title != null) {
        document.title = 'Quiz Maker for ' + theme.test_title;
      }
    } else {
      provide('show_ads', false)
      if (theme.test_title != null) {
        document.title = theme.test_title;
      }
    }

    const isMouseOnOuterBorder = computed(() => {
      return store.getters["Monitoring/isMouseOnOuterBorder"];
    });

    const isFaceOutOfWebcam = computed(() => {
      return store.getters["Monitoring/isNoFaceDetected"];
    })


    googleTagManagerUtils().pushEventData("Initiate Test", "");

    let body_background_css = '';

    if (theme.body_bg_color) {
      body_background_css = body_background_css + theme.body_bg_color;
    }

    if (theme.body_bg_img_url != null) {
      if (theme.body_bg_img_repeat) {
        body_background_css = body_background_css + ' ' + theme.body_bg_img_repeat;
      }
      body_background_css = body_background_css + " url('" + theme.body_bg_img_url + "')";
    }

    let header_log_custom_img_css = 'margin-left: ' + theme.header_content_align + ';';
    if (theme.header_content_align == '0px') {
      header_log_custom_img_css = header_log_custom_img_css + 'padding-left: 0px;';
    }


    let css_style = `
    ion-content {
      --background: ${body_background_css};
    }

    a {
      color: ${theme.default_button_color} !important;
    }

    ion-button {
      --background: ${theme.default_button_color};
    }

    ion-button.finish-button {
      --background: ${theme.finish_button_color} ;
    }

    ion-button.certificate-button {
      --background: ${theme.certificate_button_color} ;
    }

    ion-button.see-all-question-button {
      color: ${theme.see_all_question_button_color};
      --background:transparent;
    }

    ion-button.continue-button {
      --background: ${theme.return_button_color};
    }

    .template {
      margin-left: ${theme.body_content_align} !important;
    }

    #test-header{
       background-color: white;
    }

    #test-header .header{
       background-color:white !important;
    }

    .header {
      background-color: ${theme.header_bg_color};
      margin-left: ${theme.body_content_align};
      max-width: ${theme.header_width};
      margin-right: auto;
      position: relative;
    }

    .header .header__logo_custom h1 {
      padding: 16px;
      color: ${theme.header_text_color};
      background-color: ${theme.header_bg_color};
      text-align: ${theme.header_text_align};
    }

    .header .header__logo_custom img{
        ${header_log_custom_img_css}
    }`;

    let head = document.head || document.getElementsByTagName('head')[0];
    let style = document.createElement('style');

    head.appendChild(style);
    style.type = 'text/css';
    style.appendChild(document.createTextNode(css_style));


    watch(() => displayErrorPage, (to, from) => {
      /** when error page is displayed, we need to make sure few things.
       * 1. clear test auto submit timer
       * 2. send google analytics data
       */

      if (to.value == true) {
        store.dispatch('clearTestAutoSubmitTimer');
        store.dispatch('clearTestTimeoutTimer')
      }
    }, {deep: true});


    /*
      mobile detector's detect function is asynchronous for using navigator.userAgent data.
      Hence, the browser monitor will set after watch is triggered due to the asynchronous manner.
      In order to resolve this issue, browsermonitor has to be set as reference, so it's value change
      can be detected in watch{} area.
     */

    let browser_monitor = ref(null);
    let mobile_detector = new MobileDetector();

    mobile_detector.detect().then(() => {
      store.dispatch('setIsMobileDevice', mobile_detector.is_mobile);
      if (mobile_detector.is_mobile) {
        // console.debug('mobile device detected: disable mouse outside detection & full screen & focus enable')
        UseMonitorBrowserOptions.outsideEnabled = false;
        UseMonitorBrowserOptions.fullscreenEnabled = false;
        UseMonitorBrowserOptions.focusedEnabled = false;
      }


      browser_monitor.value = useMonitorBrowser({
        onError: (error) => {
          // console.log(error);
          store.dispatch('Monitoring/haltMonitoring', error, {root: true});
        }
      });
    })

    // set global emitter listener : start browser monitoring, stop browser-monitoring
    const internalInstance = getCurrentInstance();
    let globalEmitter = internalInstance.appContext.config.globalProperties.$globalEmitter

    globalEmitter.on('start-browser-monitoring', () => {
      browser_monitor.value.startMonitoring();
    })

    globalEmitter.on('stop-browser-monitoring', () => {
      browser_monitor.value.stopMonitoring();
    })


    const detectMouseLeave = () => {
      if (settings != null && settings.hasOwnProperty('proctor_user_monitor_mode') && settings.proctor_user_monitor_mode) {
        store.dispatch('Monitoring/setMouseLeaveDetected', true)
      }
    }

    const leaveWarningMessage = computed(() => {
      if (store.getters['Monitoring/isNoFaceDetected']) {
        return language_set.value['face_missing_msg'];
      } else {
        return language_set.value['mouse_cursor_out_msg'];
      }
    });

    const isMobileDevice = computed(() => {
      return store.getters['isMobileDevice'];
    })

    const isAnalyticsConsented =computed(()=>{
      return store.getters['UserConsent/getAnalytics'];
    })

    watch(isAnalyticsConsented,(value)=>{
      const gtm = useGtm();
      if(value === true && gtm.enabled()===false){
        gtm.enable(true);
      }else if(value ===false){
        gtm.enable(false);
      }
    })

    return {
      displayErrorModal,
      displayErrorPage,
      preview_mode,
      offline_status,
      STATUS_ONLINE,
      reach_sim_user_limit,
      browser_monitor,
      detectMouseLeave,
      isMouseOnOuterBorder,
      language_set,
      isFaceOutOfWebcam,
      leaveWarningMessage,
      isMobileDevice,
      constant,
      testState
    };
  }
});

</script>

<style lang="scss" scoped>


#cmTestApp {


  &.fullscreen-border {
    background: #EDEDED;
    //transition: background-color 0.7s ease-in-out;
    //transition: background-color 750ms cubic-bezier(.89,.24,.87,.86);
    transition: background-color 750ms ease-out;

    .ion-page.fullscreen-mode {
      border-radius: 16px;
      box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.09);
      margin: 60px 143px 64px 143px;

      transition: opacity 750ms ease-out;
    }


  }

  &.warning {
    background: #808080;
    cursor: not-allowed;
  }


}
</style>
